<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import type { IconProps } from './icon.types';
import { iconClasses } from './icon.styles';
import { useIconStore, type IconStore } from '@/runtime/composables/useIconStore';

const props = defineProps<IconProps>();
const iconStore: IconStore = useIconStore();

const iconEl = ref<HTMLElement | null>(null);

/**
 * Loads and renders the current icon SVG into the icon container.
 *
 * - Resolves the icon name via {@link IconStore.resolveIconName}
 * - Fetches SVG content via {@link IconStore.fetchSvgContent}
 * - Normalizes accessibility attributes and removes fixed sizing
 */
async function loadSvg(): Promise<void> {
  const el = iconEl.value;
  const name = props.name;

  if (!el || !name) return;

  const iconName = iconStore.resolveIconName(name);
  if (!iconName) {
    el.innerHTML = '';
    return;
  }

  const svgContent = await iconStore.fetchSvgContent(iconName);
  if (!svgContent) {
    el.innerHTML = '';
    return;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const svgElement = doc.querySelector('svg');

  if (!svgElement) {
    el.innerHTML = '';
    return;
  }

  svgElement.setAttribute('role', 'img');
  svgElement.setAttribute('aria-label', name);
  svgElement.removeAttribute('width');
  svgElement.removeAttribute('height');

  el.innerHTML = '';
  el.appendChild(svgElement);
}

/**
 * Fetches the icon manifest (cached in the store) and renders
 * the initial icon when the component is mounted.
 */
onMounted(async () => {
  await iconStore.fetchIconList();
  await loadSvg();
});

/**
 * Re-render the icon whenever the `name` prop changes.
 */
watch(
  () => props.name,
  () => {
    void loadSvg();
  }
);
</script>

<template>
  <div
    ref="iconEl"
    class="e-icon"
    :class="iconClasses(props)"
  />
</template>
